/**
 * CueEngine (ARCHITECTURE §11.4, §5.9): the pure schedule from @lw/shared, a 1.5 s lookahead
 * scheduler on the audio clock, 10 s re-anchoring with the last-second precedence, the cue ledger
 * (no double play across refresh or tab handover), the degraded-cue policy, abort-with-ledger on
 * cancel, the `lw-cue-leader` multi-tab lock with visibility-aware handover, and the event-driven
 * callouts (CHECK FIRE, CEASE LASING, LASER OFF).
 */
import { browserClock } from '@lw/shared/clock/browser';
import type { ClockPort } from '@lw/shared/clock';
import { AUDIO_REANCHOR_MS, CUE_LEADER_STALE_MS, IMPACT_TONE_HZ, LASER_TONE_HZ } from '@lw/shared/constants';
import { RAMP_BEEP_DURATION_MS, rampOnsets, reanchorDecision, computeCueSchedule, type CueId, type ScheduledCue } from '@lw/shared/time-sync';
import type { ClientSnapshot, ClientEvent } from '@lw/client-core';
import type { AudioEngine, NodeHandle } from './AudioEngine.js';
import type { Runtime } from '../runtime/runtime.js';

export type LedgerEntry = { status: 'PLAYED'; at: number; late?: boolean; lateByMs?: number; degraded?: boolean } | { status: 'ABORTED'; playedMs: number } | { status: 'MISSED'; lateByMs: number };

export interface CueLogLine {
  at: number;
  type: 'CUE_SCHEDULED' | 'CUE_PLAYED' | 'CUE_PLAYED_DEGRADED' | 'CUE_ABORTED' | 'CUE_MISSED' | 'CUE_RESCHEDULED' | 'CUE_LEADER_CHANGED' | 'AUDIO_SELFTEST';
  cueId?: string;
  detail?: Record<string, unknown>;
}

interface Armed {
  cue: ScheduledCue;
  /** per node: the scheduled audio time and handle */
  nodes: { onsetServer: number; audioT: number; handle: NodeHandle }[];
  scheduledAt: number;
}

export class CueEngine {
  private readonly rt: Runtime;
  private readonly audio: AudioEngine;
  private readonly clock: ClockPort;
  private readonly ledgerStore: Runtime['stores']['cueLedger'];
  private readonly onLog: (t: string, cls?: 'warn' | 'bad' | 'good') => void;
  readonly cueLog: CueLogLine[] = [];
  private timelineKey: string | null = null;
  private schedule: ScheduledCue[] = [];
  private armed = new Map<CueId, Armed>();
  private ledger: Record<string, LedgerEntry | number> = {};
  private missionId: string | null = null;
  private tickHandle: number | null = null;
  private lastReanchor = 0;
  private lastEventSeq = 0;
  private lastState: string | null = null;
  // leadership
  leader = false;
  private channel: BroadcastChannel | null = null;
  private heartbeat: number | null = null;
  private lastLeaderBeat = 0;
  private lockRelease: (() => void) | null = null;
  private wakeLock: { release(): Promise<void> } | null = null;

  constructor(opts: { runtime: Runtime; audio: AudioEngine; clock: ClockPort; ledger: Runtime['stores']['cueLedger']; onLog: (t: string, cls?: 'warn' | 'bad' | 'good') => void }) {
    this.rt = opts.runtime;
    this.audio = opts.audio;
    this.clock = opts.clock;
    this.ledgerStore = opts.ledger;
    this.onLog = opts.onLog;
  }

  /** one leader per station: tabs of the same role and device on this browser compete, others do not */
  private get lockName(): string {
    return `lw-cue-leader:${this.rt.id.role}:${this.rt.id.deviceId}`;
  }

  start(): void {
    this.tickHandle = window.setInterval(() => this.tick(), 100);
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(this.lockName);
      this.channel.onmessage = (ev: MessageEvent<{ type: 'beat'; at: number }>) => {
        if (ev.data?.type === 'beat') this.lastLeaderBeat = browserClock.perfNow();
      };
    }
    if (!document.hidden) void this.requestLeadership(false);
  }

  // ── leadership (§11.4 multi-tab) ─────────────────────────────────────────────────────────────
  private wantLock = false;
  private lockAbort: AbortController | null = null;

  private requestLeadership(steal: boolean): void {
    this.wantLock = true;
    const locks = navigator.locks;
    if (!locks) {
      this.becomeLeader();
      return;
    }
    if (this.lockAbort) return; // a request is already pending, or we already hold the lock
    const abort = new AbortController();
    this.lockAbort = abort;
    // wait for the lock (so a backgrounded holder handing off releases to us) unless we are stealing
    // a stale/dead leader's lock; steal cannot be combined with an abort signal
    const opts: LockOptions = steal ? { mode: 'exclusive', steal: true } : { mode: 'exclusive', signal: abort.signal };
    locks
      .request(this.lockName, opts, async (lock) => {
        if (!lock || !this.wantLock) return; // no lock, or we no longer want it → release at once
        this.becomeLeader();
        await new Promise<void>((resolve) => (this.lockRelease = resolve));
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.lockAbort === abort) this.lockAbort = null;
        this.lockRelease = null;
        if (this.leader) {
          this.leader = false;
          this.log({ type: 'CUE_LEADER_CHANGED', detail: { leader: false } });
        }
      });
  }

  private becomeLeader(): void {
    if (this.leader) return;
    this.leader = true;
    this.log({ type: 'CUE_LEADER_CHANGED', detail: { leader: true } });
    this.onLog('audio on this tab', 'good');
    this.heartbeat = window.setInterval(() => this.channel?.postMessage({ type: 'beat', at: browserClock.perfNow() }), 1000);
    this.rearmFromLedger();
  }

  private resignLeadership(): void {
    this.wantLock = false;
    if (this.heartbeat !== null) clearInterval(this.heartbeat);
    this.heartbeat = null;
    if (this.leader) {
      this.abortAll('LEADER_HANDOVER');
      this.leader = false;
      this.log({ type: 'CUE_LEADER_CHANGED', detail: { leader: false } });
    }
    this.lockRelease?.(); // release if we hold it → a waiting tab acquires it
    this.lockRelease = null;
    this.lockAbort?.abort(); // cancel if we are still waiting for it
  }

  onHidden(): void {
    this.resignLeadership();
  }

  onVisible(): void {
    if (this.leader) return;
    const stale = browserClock.perfNow() - this.lastLeaderBeat > CUE_LEADER_STALE_MS;
    void this.requestLeadership(stale);
  }

  // ── timeline ─────────────────────────────────────────────────────────────────────────────────
  onSnapshot(s: ClientSnapshot): void {
    const tl = s.timeline;
    const key = s.state === 'IN_FLIGHT' && tl ? `${s.missionId}:${tl.t0}:${tl.tLaserOn}:${tl.tImpact}` : null;
    if (s.missionId !== this.missionId) {
      this.missionId = s.missionId;
      this.ledger = s.missionId ? (this.ledgerStore.load(s.missionId) as Record<string, LedgerEntry | number>) : {};
    }
    if (this.lastState === 'IN_FLIGHT' && s.state !== 'IN_FLIGHT' && s.state !== 'AWAITING_DISPOSITION') {
      // cancel: live nodes stop with the 20 ms ramp and the ledger says what was heard
      this.abortAll('CANCELLED');
    }
    this.lastState = s.state;
    if (key !== this.timelineKey) {
      this.abortAll('TIMELINE_CHANGED');
      this.timelineKey = key;
      this.schedule = [];
      if (key && tl) {
        const now = this.rt.client.chrono.serverNow();
        if (now === null) return;
        const sightKey = `firstSight:${tl.t0}`;
        const first = typeof this.ledger[sightKey] === 'number' ? (this.ledger[sightKey] as number) : now;
        if (typeof this.ledger[sightKey] !== 'number') this.saveLedger(sightKey, first);
        this.schedule = computeCueSchedule(tl, this.rt.id.role === 'ADMIN' ? 'ADMIN' : this.rt.id.role, first);
        for (const c of this.schedule) if (c.status === 'MISSED' && !this.ledger[c.id]) this.saveLedger(c.id, { status: 'MISSED', lateByMs: c.lateByMs ?? 0 });
        this.requestWakeLock();
      } else {
        void this.wakeLock?.release();
        this.wakeLock = null;
      }
    }
  }

  /** Event-driven callouts (never replayed: keyed by event seq in the ledger). */
  onMissionEvents(events: ClientEvent[]): void {
    for (const e of events) {
      if (e.seq <= this.lastEventSeq) continue;
      this.lastEventSeq = e.seq;
      const k = `ev:${e.seq}`;
      if (this.ledger[k]) continue;
      const role = this.rt.id.role;
      const now = this.audio.now() + 0.05;
      if (e.type === 'FIRE_CANCELLED' && this.leader && this.audio.armed) {
        this.audio.twoTone(now);
        const band = e.payload['band'];
        this.audio.voice(now + 0.8, band === 'CHECK_FIRE' && role === 'UAV' ? 'cease-lasing' : 'check-fire', band === 'CHECK_FIRE' && role === 'UAV' ? 'Check fire. Cease lasing.' : 'Check fire.');
        this.audio.vibrate([200, 100, 200, 100, 400]);
        this.saveLedger(k, { status: 'PLAYED', at: this.serverNowOr0() });
        this.log({ type: 'CUE_PLAYED', cueId: 'check-fire' });
      }
      if (e.type === 'LASER_OFF_CONFIRMED' && this.leader && this.audio.armed) {
        this.audio.voice(now, 'laser-off', 'Laser off.');
        this.saveLedger(k, { status: 'PLAYED', at: this.serverNowOr0() });
        this.log({ type: 'CUE_PLAYED', cueId: 'laseroff-callout' });
      }
    }
  }

  onClockStep(deltaMs: number): void {
    for (const a of this.armed.values()) this.reanchor(a, 'CLOCK_STEP', Math.abs(deltaMs));
  }

  /**
   * The AudioContext was suspended/interrupted by the OS (tab backgrounded on iOS) or resumed.
   * While it is not running, drop every scheduled node without a ledger write so nothing is queued
   * inside a suspended context (which would all fire at once on resume) and so the tick re-schedules
   * each cue fresh, from its current position, once audio is armed again.
   */
  onAudioStateChange(): void {
    if (this.audio.armed) return; // resumed: the 100 ms tick re-arms un-ledgered cues at the current position
    if (this.armed.size === 0) return;
    for (const [, a] of this.armed) for (const n of a.nodes) n.handle.stop(0);
    this.armed.clear();
    this.onLog('audio interrupted — tap to re-arm', 'warn');
  }

  /** Audio self-test (§11.4). Returns the handle so the UI can wait for it. */
  selfTest(): NodeHandle {
    this.log({ type: 'AUDIO_SELFTEST', detail: { played: true } });
    return this.audio.testTone();
  }

  recordSelfTest(result: 'HEARD' | 'SKIPPED'): void {
    this.log({ type: 'AUDIO_SELFTEST', detail: { result } });
    this.onLog(`audio self-test: ${result}`, result === 'HEARD' ? 'good' : 'warn');
  }

  // ── scheduler ────────────────────────────────────────────────────────────────────────────────
  private tick(): void {
    if (!this.audio.armed && this.armed.size) {
      // the OS interrupted the context (or the gate is up): drop everything queued, don't ledger it
      for (const [, a] of this.armed) for (const n of a.nodes) n.handle.stop(0);
      this.armed.clear();
    }
    if (!this.leader || this.schedule.length === 0) return;
    const now = this.rt.client.chrono.serverNow();
    if (now === null) return;
    const degraded = this.rt.client.chrono.currentMode() !== 'NORMAL';
    for (const cue of this.schedule) {
      if (this.armed.has(cue.id) || this.ledger[cue.id]) continue;
      if (cue.status === 'MISSED') continue;
      const onset = cue.status === 'LATE_PARTIAL' && cue.enterAt !== undefined ? Math.max(cue.at, cue.enterAt) : cue.at;
      if (cue.until <= now) {
        this.saveLedger(cue.id, { status: 'MISSED', lateByMs: now - cue.at });
        this.log({ type: 'CUE_MISSED', cueId: cue.id, detail: { lateByMs: now - cue.at } });
        continue;
      }
      if (onset - now > 1500) continue; // outside the lookahead
      if (!this.audio.armed) continue; // TAP TO ARM gate; the visuals are state-derived anyway
      this.arm(cue, now, degraded);
    }
    // re-anchor pass (§5.9)
    const perf = this.clock.perfNow();
    if (perf - this.lastReanchor >= AUDIO_REANCHOR_MS) {
      this.lastReanchor = perf;
      for (const a of this.armed.values()) this.reanchor(a, 'PERIODIC', 0);
    }
    // completed cues → PLAYED
    for (const [id, a] of this.armed) {
      if (now >= a.cue.until + 50) {
        this.armed.delete(id);
        const entry: LedgerEntry = { status: 'PLAYED', at: a.cue.at, ...(a.cue.lateByMs ? { late: true, lateByMs: a.cue.lateByMs } : {}), ...(degraded ? { degraded: true } : {}) };
        this.saveLedger(id, entry);
        this.log({ type: degraded ? 'CUE_PLAYED_DEGRADED' : 'CUE_PLAYED', cueId: id, detail: { lateByMs: a.cue.lateByMs ?? 0 } });
      }
    }
  }

  private audioT(serverT: number, now: number): number {
    return this.audio.now() + (serverT - now) / 1000;
  }

  private arm(cue: ScheduledCue, now: number, degraded: boolean): void {
    const nodes: Armed['nodes'] = [];
    const at = (t: number): number => this.audioT(t, now);
    const mk = (onsetServer: number, handle: NodeHandle): void => void nodes.push({ onsetServer, audioT: at(onsetServer), handle });
    switch (cue.id) {
      case 'fire-alarm':
        this.audio.alarm(at(cue.at)).forEach((h, i) => mk(cue.at + i * 300, h));
        this.audio.vibrate([200, 100, 200, 100, 200]);
        break;
      case 'shot-late':
        this.audio.twoTone(at(cue.at)).forEach((h, i) => mk(cue.at + i * 300, h));
        mk(cue.at + 800, this.audio.voice(at(cue.at + 800), 'shot-in-flight', 'Shot. In flight.'));
        this.audio.vibrate([300, 100, 300]);
        break;
      case 'lasing-callout':
        mk(cue.at, this.audio.voice(at(cue.at), 'lasing', 'Lasing.'));
        break;
      case 'laser-warn':
      case 'impact-warn': {
        const hz = cue.id === 'laser-warn' ? LASER_TONE_HZ : IMPACT_TONE_HZ;
        for (const o of rampOnsets(cue.until)) {
          if (o + RAMP_BEEP_DURATION_MS <= now) continue; // entering mid-window: remaining beeps only
          mk(o, this.audio.beep(at(o), hz, RAMP_BEEP_DURATION_MS));
        }
        break;
      }
      case 'laser-mark':
      case 'impact-mark': {
        const hz = cue.id === 'laser-mark' ? LASER_TONE_HZ : IMPACT_TONE_HZ;
        const late = Math.max(0, now - cue.at);
        mk(cue.at + late, this.audio.tone(at(cue.at + late), hz, Math.max(200, cue.until - cue.at - late)));
        this.audio.vibrate(cue.id === 'impact-mark' ? [500] : [250]);
        break;
      }
      case 'laser-callout':
        mk(cue.at, this.audio.voice(at(Math.max(cue.at, now)), 'laser-on', 'Laser on.'));
        break;
    }
    this.armed.set(cue.id, { cue, nodes, scheduledAt: now });
    this.log({ type: 'CUE_SCHEDULED', cueId: cue.id, detail: { at: cue.at, status: cue.status, degraded, lateByMs: cue.lateByMs ?? 0 } });
  }

  private reanchor(a: Armed, reason: 'PERIODIC' | 'CLOCK_STEP', stepMs: number): void {
    const now = this.rt.client.chrono.serverNow();
    if (now === null) return;
    let moved = 0;
    for (const n of a.nodes) {
      if (now >= n.onsetServer + n.handle.durationMs) continue; // already over
      const want = this.audioT(n.onsetServer, now);
      const err = Math.abs(want - n.audioT) * 1000;
      const d = reanchorDecision({ reason, timeToOnsetMs: n.onsetServer - now, errorMs: reason === 'PERIODIC' ? err : stepMs });
      if (d === 'KEEP') continue;
      n.handle.stop(20);
      const hz = a.cue.id.startsWith('laser') ? LASER_TONE_HZ : IMPACT_TONE_HZ;
      const remaining = n.onsetServer + n.handle.durationMs - Math.max(now, n.onsetServer);
      n.handle = n.handle.durationMs > 1000 ? this.audio.tone(want, hz, remaining) : this.audio.beep(want, hz, n.handle.durationMs);
      n.audioT = want;
      moved += 1;
    }
    if (moved) this.log({ type: 'CUE_RESCHEDULED', cueId: a.cue.id, detail: { reason, nodes: moved, stepMs } });
  }

  private abortAll(reason: string): void {
    const now = this.serverNowOr0();
    for (const [id, a] of this.armed) {
      let playedMs = 0;
      for (const n of a.nodes) playedMs += n.handle.stop(20);
      if (reason !== 'LEADER_HANDOVER') {
        this.saveLedger(id, { status: 'ABORTED', playedMs: Math.round(playedMs) });
        this.log({ type: 'CUE_ABORTED', cueId: id, detail: { reason, playedMs: Math.round(playedMs), at: now } });
      }
    }
    this.armed.clear();
  }

  /** A new leader enters windowed cues at the current position and skips ledgered point cues. */
  private rearmFromLedger(): void {
    if (this.missionId) this.ledger = this.ledgerStore.load(this.missionId) as Record<string, LedgerEntry | number>;
    this.armed.clear();
  }

  private saveLedger(key: string, v: LedgerEntry | number): void {
    this.ledger[key] = v;
    if (this.missionId) this.ledgerStore.save(this.missionId, this.ledger);
  }

  ledgerEntry(id: string): LedgerEntry | number | undefined {
    return this.ledger[id];
  }

  /** test instrumentation: how many cue groups currently hold live audio nodes. */
  armedCount(): number {
    return this.armed.size;
  }

  private serverNowOr0(): number {
    return this.rt.client.chrono.serverNow() ?? 0;
  }

  private requestWakeLock(): void {
    const wl = (navigator as unknown as { wakeLock?: { request(t: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock;
    if (!wl || this.wakeLock) return;
    wl.request('screen').then((l) => (this.wakeLock = l)).catch(() => undefined);
  }

  private log(l: Omit<CueLogLine, 'at'>): void {
    this.cueLog.push({ at: this.serverNowOr0(), ...l });
    if (this.cueLog.length > 500) this.cueLog.shift();
  }

  stop(): void {
    if (this.tickHandle !== null) clearInterval(this.tickHandle);
    this.resignLeadership();
  }
}
